// The skill ladder and the adaptive rule. One rating per skill, rungs 1..10.

import type { SkillId, Category } from './types';

export const MIN_RUNG = 1;
export const MAX_RUNG = 10;
export const DEFAULT_RUNG = 3;

export interface SkillState {
  rung: number;
  up: number;        // consecutive clean corrects at this rung
  down: number;      // consecutive wrongs
  recent: boolean[]; // last 5 outcomes (hinted corrects excluded)
  attempts: number;
  correct: number;
}

export interface SkillDef { id: SkillId; label: string; category: Category; kid: string }

export const SKILLS: SkillDef[] = [
  { id: 'add_sub', label: 'Addition and subtraction', category: 'math', kid: 'Adding and taking away' },
  { id: 'mult_div', label: 'Multiplication and division', category: 'math', kid: 'Times and sharing' },
  { id: 'place_value', label: 'Place value', category: 'math', kid: 'Big numbers' },
  { id: 'fractions', label: 'Fractions', category: 'math', kid: 'Fractions' },
  { id: 'algebra', label: 'Algebra', category: 'math', kid: 'Mystery numbers' },
  { id: 'geometry', label: 'Geometry', category: 'math', kid: 'Shapes' },
  { id: 'measure_time_money', label: 'Measurement, time and money', category: 'math', kid: 'Clocks, coins and rulers' },
  { id: 'data', label: 'Data and graphs', category: 'math', kid: 'Graphs' },
  { id: 'logic', label: 'Logic and patterns', category: 'math', kid: 'Patterns' },
  { id: 'phonics_spelling', label: 'Phonics and spelling', category: 'words', kid: 'Spelling' },
  { id: 'vocabulary', label: 'Vocabulary', category: 'words', kid: 'Word power' },
  { id: 'grammar', label: 'Grammar', category: 'words', kid: 'Sentences' },
  { id: 'comprehension', label: 'Reading comprehension', category: 'words', kid: 'Reading' },
  { id: 'word_play', label: 'Word play', category: 'words', kid: 'Word games' },
  { id: 'geology', label: 'Geology', category: 'science', kid: 'Rocks' },
  { id: 'paleontology', label: 'Paleontology', category: 'science', kid: 'Fossils' },
  { id: 'chemistry', label: 'Chemistry', category: 'science', kid: 'Atoms and stuff' },
  { id: 'biology', label: 'Biology', category: 'science', kid: 'Living things' },
  { id: 'physics', label: 'Physics', category: 'science', kid: 'Forces' },
  { id: 'space', label: 'Space', category: 'science', kid: 'Space' },
  { id: 'earth_science', label: 'Earth science', category: 'science', kid: 'Weather and water' },
  { id: 'history', label: 'History', category: 'science', kid: 'Long ago' },
];

export const SKILL_BY_ID: Record<SkillId, SkillDef> = Object.fromEntries(SKILLS.map(s => [s.id, s])) as any;

export function freshSkill(rung = DEFAULT_RUNG): SkillState {
  return { rung, up: 0, down: 0, recent: [], attempts: 0, correct: 0 };
}

export function gradeFor(rung: number): string {
  if (rung <= 2) return '2nd';
  if (rung <= 5) return '3rd';
  if (rung <= 7) return '4th';
  if (rung <= 9) return '5th';
  return '6th';
}

/** She sees a ring color, never a number. */
export function ringFor(rung: number): { name: string; color: string } {
  if (rung <= 2) return { name: 'White', color: '#f4f1ea' };
  if (rung <= 4) return { name: 'Yellow', color: '#f5c542' };
  if (rung <= 6) return { name: 'Green', color: '#5fbf6a' };
  if (rung <= 8) return { name: 'Blue', color: '#4f8fe6' };
  if (rung <= 9) return { name: 'Purple', color: '#9b6be6' };
  return { name: 'Gold', color: '#e0a526' };
}

export interface AdaptResult { before: number; after: number; moved: -1 | 0 | 1 }

/**
 * The rule: up one rung after 3 consecutive clean corrects; down one after
 * 2 consecutive wrongs or 3 of the last 5 wrong. A hinted correct is neutral.
 */
export function applyAttempt(
  s: SkillState,
  correct: boolean,
  hintUsed: boolean,
  pin?: [number, number],
): AdaptResult {
  const before = s.rung;
  s.attempts++;
  if (correct) s.correct++;
  const lo = Math.max(MIN_RUNG, pin?.[0] ?? MIN_RUNG);
  const hi = Math.min(MAX_RUNG, pin?.[1] ?? MAX_RUNG);

  if (correct && hintUsed) {
    // neutral: breaks a wrong streak, does not count toward promotion
    s.down = 0;
  } else if (correct) {
    s.up++;
    s.down = 0;
    s.recent.push(true);
  } else {
    s.down++;
    s.up = 0;
    s.recent.push(false);
  }
  if (s.recent.length > 5) s.recent.shift();

  let moved: -1 | 0 | 1 = 0;
  const wrongOfFive = s.recent.length >= 5 ? s.recent.filter(x => !x).length : 0;
  if (s.up >= 3 && s.rung < hi) {
    s.rung++; moved = 1; s.up = 0; s.down = 0; s.recent = [];
  } else if ((s.down >= 2 || wrongOfFive >= 3) && s.rung > lo) {
    s.rung--; moved = -1; s.up = 0; s.down = 0; s.recent = [];
  }
  // pins can also pull an existing rung into range
  if (s.rung < lo) s.rung = lo;
  if (s.rung > hi) s.rung = hi;
  return { before, after: s.rung, moved };
}

export function clampToGame(rung: number, range: [number, number]): number {
  return Math.max(range[0], Math.min(range[1], rung));
}
